import { motion } from "framer-motion";
import { Heart, Download, MessageSquare } from "lucide-react";
import ChapterDivider from "../shared/ChapterDivider";
import FooterSignature from "../shared/FooterSignature";
import PremiumCover from "../shared/PremiumCover";
import InteractiveHeart from "../shared/InteractiveHeart";
import useHoverPrefetch from "../shared/useHoverPrefetch";
import ShimmerImg from "../shared/ShimmerImg";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Editorial template — magazine-style with asymmetric mixed layouts.
 *
 * Within a chapter we batch photos into "spreads" of 5: hero (full bleed) →
 * paired-spread (2 side by side) → triptych row (2). This keeps the page feeling
 * curated rather than a uniform grid, while still scaling to large galleries.
 */
export default function EditorialTemplate({ gallery, accent, ui, actions }) {
  const photos = gallery.photos || [];
  const chapters = (gallery.chapters || []).slice().sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  const grouped = groupByChapter(photos, chapters);

  return (
    <div className="bg-stone-50 text-stone-900 min-h-screen" style={{ fontFamily: "var(--gallery-font-sans)" }} data-testid="editorial-template">
      <PremiumCover gallery={gallery} accent={accent} selectedCount={ui.selectedCount} />

      {gallery.welcome_message && (
        <div className="max-w-2xl mx-auto px-6 pt-20 pb-10 text-center">
          <p
            className="text-base sm:text-lg leading-relaxed text-stone-600"
            style={{ fontFamily: "var(--gallery-font-serif)" }}
          >
            {gallery.welcome_message}
          </p>
        </div>
      )}

      {ui.phaseBanner}
      {ui.downloadBar}
      {ui.interactive?.headerStrip}

      <div className="max-w-6xl mx-auto px-4 sm:px-8">
        {grouped.map((group, gi) => (
          <section key={group.key} className="pb-12">
            {group.chapter && <ChapterDivider chapter={group.chapter} variant="editorial" index={gi - (grouped[0].chapter ? 0 : 1)} />}
            <EditorialSpreads photos={group.photos} actions={actions} ui={ui} watermark={gallery.watermark_enabled} />
          </section>
        ))}
      </div>

      {ui.selectionBar}
      <FooterSignature gallery={gallery} variant="light" />
    </div>
  );
}

function groupByChapter(photos, chapters) {
  if (!chapters.length) return [{ key: "all", chapter: null, photos }];
  const byId = Object.fromEntries(chapters.map((c) => [c.id, []]));
  const unassigned = [];
  for (const p of photos) {
    if (p.chapter_id && byId[p.chapter_id]) byId[p.chapter_id].push(p);
    else unassigned.push(p);
  }
  const out = [];
  if (unassigned.length) out.push({ key: "intro", chapter: null, photos: unassigned });
  for (const c of chapters) {
    out.push({ key: c.id, chapter: c, photos: byId[c.id] });
  }
  return out.filter((g) => g.photos.length > 0);
}

function EditorialSpreads({ photos, actions, ui, watermark }) {
  // Build a heterogeneous list of spread rows from a flat photo array.
  // Pattern: hero / pair / pair / hero / triplet …
  const rows = [];
  let i = 0;
  let beat = 0;
  while (i < photos.length) {
    const remaining = photos.length - i;
    const shape = beat % 4;
    if (shape === 0 || remaining === 1) {
      rows.push({ kind: "hero", photos: photos.slice(i, i + 1) });
      i += 1;
    } else if (shape === 1) {
      const n = Math.min(2, remaining);
      rows.push({ kind: "pair", photos: photos.slice(i, i + n) });
      i += n;
    } else if (shape === 2) {
      const n = Math.min(3, remaining);
      rows.push({ kind: "triplet", photos: photos.slice(i, i + n) });
      i += n;
    } else {
      const n = Math.min(2, remaining);
      rows.push({ kind: "pair-offset", photos: photos.slice(i, i + n) });
      i += n;
    }
    beat += 1;
  }

  return (
    <div className="space-y-10 sm:space-y-16">
      {rows.map((row, idx) => (
        <SpreadRow key={`${row.kind}-${idx}`} row={row} actions={actions} ui={ui} watermark={watermark} />
      ))}
    </div>
  );
}

function SpreadRow({ row, actions, ui, watermark }) {
  if (row.kind === "hero") {
    const p = row.photos[0];
    return (
      <PhotoFrame photo={p} actions={actions} ui={ui} watermark={watermark} aspect="aspect-[16/10]" testid={`editorial-hero-${p.id}`} />
    );
  }
  if (row.kind === "pair") {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-10 lg:gap-12">
        {row.photos.map((p) => (
          <PhotoFrame key={p.id} photo={p} actions={actions} ui={ui} watermark={watermark} aspect="aspect-[3/4]" testid={`editorial-pair-${p.id}`} />
        ))}
      </div>
    );
  }
  if (row.kind === "pair-offset") {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-6 sm:gap-10 lg:gap-12">
        {row.photos[0] && (
          <div className="sm:col-span-3">
            <PhotoFrame photo={row.photos[0]} actions={actions} ui={ui} watermark={watermark} aspect="aspect-[4/3]" testid={`editorial-offsetA-${row.photos[0].id}`} />
          </div>
        )}
        {row.photos[1] && (
          <div className="sm:col-span-2 sm:pt-12">
            <PhotoFrame photo={row.photos[1]} actions={actions} ui={ui} watermark={watermark} aspect="aspect-[3/4]" testid={`editorial-offsetB-${row.photos[1].id}`} />
          </div>
        )}
      </div>
    );
  }
  if (row.kind === "triplet") {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-6 sm:gap-10 lg:gap-12">
        {row.photos.map((p) => (
          <PhotoFrame key={p.id} photo={p} actions={actions} ui={ui} watermark={watermark} aspect="aspect-square" testid={`editorial-trip-${p.id}`} />
        ))}
      </div>
    );
  }
  return null;
}

function PhotoFrame({ photo, actions, ui, watermark, aspect = "aspect-square", testid }) {
  const fav = ui.favorites?.[photo.id];
  const isFav = !!fav?.favorited;
  const hasNote = !!fav?.comment;
  const hover = useHoverPrefetch(photo.url);
  // Respect reduced-motion: skip the heavy initial reveal + layoutId morph
  const reduced = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  return (
    <motion.div
      layoutId={reduced ? undefined : `gallery-photo-${photo.id}`}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={reduced
        ? { duration: 0.2 }
        : { layout: { type: "spring", stiffness: 280, damping: 36 }, duration: 0.6, ease: "easeOut" }}
      className={`relative group bg-stone-100 ${aspect} overflow-hidden`}
      onMouseEnter={hover.onMouseEnter}
      onMouseLeave={hover.onMouseLeave}
      // Virtualization for large galleries: skip layout + paint for tiles
      // that aren't near the viewport. Browser reserves the aspect box (no
      // scroll jump) but skips rendering the img + interactive layers
      // until they're about to enter view. Graceful fallback on older
      // browsers (property just ignored).
      style={{ contentVisibility: "auto", containIntrinsicSize: "800px 1000px" }}
      data-testid={testid}
    >
      <button
        onClick={() => actions.openLightbox(photo)}
        className="block w-full h-full"
      >
        <ShimmerImg
          wrapperClassName="w-full h-full"
          src={`${BACKEND}${photo.url}${photo.url?.includes("?") ? "&" : "?"}w=1200&q=82`}
          alt={photo.filename}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]"
        />
        {watermark && (
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center text-white/15 text-3xl font-light tracking-[0.3em] uppercase rotate-[-15deg]">
            Preview
          </div>
        )}
      </button>
      {ui.interactive?.enabled && (
        <div className="absolute top-3 right-3 z-10 flex items-center gap-1.5" data-testid={`int-fav-${photo.id}`}>
          {ui.interactive.renderCommentBadge?.(photo)}
          <InteractiveHeart
            isMine={ui.interactive.isMine(photo.id)}
            count={ui.interactive.getEntry(photo.id).count}
            collaborators={ui.interactive.getEntry(photo.id).collaborators}
            onToggle={() => ui.interactive.toggle(photo.id)}
            testid={`fav-btn-${photo.id}`}
          />
        </div>
      )}
      {ui.interactive?.renderLiveOverlay?.(photo)}
      {!ui.isDelivered && !ui.interactive?.enabled && (
        <button
          onClick={() => actions.toggleFav(photo.id)}
          className={`absolute top-3 right-3 w-10 h-10 inline-flex items-center justify-center transition-all backdrop-blur ${
            isFav ? "text-white" : "bg-white/70 hover:bg-white text-stone-800"
          }`}
          style={isFav ? { backgroundColor: "var(--gallery-accent, #C9A961)" } : undefined}
          aria-label="Favorite"
          data-testid={`fav-btn-${photo.id}`}
        >
          <Heart className={`w-4 h-4 ${isFav ? "fill-white" : ""}`} />
        </button>
      )}
      {ui.isDelivered && ui.downloadsAvailable && (
        <button
          onClick={() => actions.downloadOne(photo)}
          className="absolute top-3 right-3 w-10 h-10 inline-flex items-center justify-center bg-white/85 hover:bg-white text-stone-800 backdrop-blur"
          aria-label="Download"
          data-testid={`dl-btn-${photo.id}`}
        >
          <Download className="w-4 h-4" />
        </button>
      )}
      {hasNote && (
        <div className="absolute bottom-3 left-3 inline-flex items-center gap-1 px-2 py-0.5 bg-white/90 text-[10px] uppercase tracking-[0.18em] backdrop-blur">
          <MessageSquare className="w-3 h-3" /> note
        </div>
      )}
    </motion.div>
  );
}
